import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pathname } from "../../../constants/pathnames/pathname";
import { Box, Grid } from "@mui/material";
import { styled } from "@mui/material";
import images from "../../../assets/images";
import DynamicInput from "../../../components/form-control/form-builder/form-builder-element/text";
import { useForm } from "react-hook-form";
import Typography from "../../../components/typography/typography";
import Button from "../../../components/button/button";
import DynamicCheckBox from "../../../components/form-control/form-builder/form-builder-element/checkbox";
import { Eye, EyeOff } from "../../../components/icons";
import { LoginFormValues } from '@erpsquad/common/runtime';
import { useAuth } from '@erpsquad/common/runtime';
import Toast from "../../../components/toast/toast";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getApiConfig } from "@/utils";

const LoginContainer = styled(Grid)(() => ({
  height: "100vh",
}));

const LeftGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
  backgroundImage: `url(${images.login.LoginLeftBg})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  [theme.breakpoints.down("lg")]: {
    "& img": {
      width: 350,
      height: 350,
    },
  },
  [theme.breakpoints.down("md")]: {
    "& img": {
      width: 300,
      height: 300,
    },
  },
}));

const RightGrid = styled(Grid)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "1rem",
}));

const FlexBox = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));

interface FormValeus {
  login: LoginFormValues;
}

interface IToast {
  message: string;
  type: "normal" | "alert";
}

const validationSchema = Yup.object({
  login: Yup.object({
    email: Yup.string()
      .required("Please enter email")
      .email("Provided email is invalid"),
    password: Yup.string().required("Please enter password"),
  }),
});

export const Login = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [_, setToken] = useState("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [toast, setToast] = useState<IToast | null>(null);
  const { user } = useAuth();

  const { login } = useAuth();

  const handleShowPassword = () => setShowPassword(!showPassword);

  const { control, handleSubmit } = useForm<FormValeus>({
    resolver: yupResolver(validationSchema),
    mode: "all",
  });

  const onSubmit = async (values: FormValeus) => {
    await login(values.login, (err) => {
      setToast({ message: err, type: "alert" });
    });
  };

  const handleForgotPassword = () => {
    navigate(Pathname.FORGOT_PASSWORD);
  }

  useEffect(() => {
    const _tid = localStorage.getItem("_tid");
    if (_tid !== null && user) {
      setToken(_tid);
      navigate(Pathname.DASHBOARD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const appLogo = getApiConfig().appLogo || images.erp_logo

  return (
    <Box>
      <LoginContainer container>
        <LeftGrid item sm={6}>
          <img src={images.login.LoginLeftSiteImage} />
        </LeftGrid>
        <RightGrid item sm={6} xs={12}>
          <FlexBox
            display="flex"
            flexDirection="column"
            gap={4}
            sx={(theme) => ({
              minWidth: 400,
              [theme.breakpoints.down("md")]: {
                minWidth: 300,
              },
              [theme.breakpoints.down("sm")]: {
                minWidth: 300,
              },
            })}
          >
            <Box textAlign="center">
              <img src={appLogo} width={160} height={55}/>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FlexBox gap={4}>
                <FlexBox gap={3}>
                  <Box>
                    <Typography
                      align="center"
                      type="h1"
                      weight="medium"
                      color="theme.secondary.1000"
                    >
                      Welcome!
                    </Typography>
                    <Typography
                      align="center"
                      type="h5"
                      weight="normal"
                      color="theme.secondary81000"
                    >
                      Enter your details below
                    </Typography>
                  </Box>
                  <FlexBox gap={2}>
                    <Box>
                      <DynamicInput
                        formControl={control}
                        label="Email"
                        name="email"
                        placeholder="Enter email"
                        fieldArrayName="login"
                      />
                    </Box>
                    <Box>
                      <DynamicInput
                        formControl={control}
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        fieldArrayName="login"
                        endIcon={
                          <>
                            {showPassword ? (
                              <Eye fontSize="small" htmlColor="#292D32" />
                            ) : (
                              <EyeOff fontSize="small" htmlColor="#292D32" />
                            )}
                          </>
                        }
                        onIconClick={handleShowPassword}
                        placeholder="Enter password"
                      />
                    </Box>
                  </FlexBox>
                </FlexBox>
                <FlexBox gap={2}>
                  <Button type="submit" size="large" variant="contained">
                    Login
                  </Button>
                  <Box display="flex" justifyContent="space-between">
                    <DynamicCheckBox
                      formControl={control}
                      title="Remember me"
                      fieldArrayName="login"
                      name="remember_me"
                    />
                    <Button
                      variant="text"
                      sx={(theme) => ({
                        color: `${theme.palette.theme?.primary["700"]} !important`,
                      })}
                      onClick = {handleForgotPassword}
                    >
                      Forgot Password
                    </Button>
                  </Box>
                </FlexBox>
              </FlexBox>
            </form>
          </FlexBox>
        </RightGrid>
      </LoginContainer>
      <Toast
        open={Boolean(toast)}
        message={toast?.message}
        type={toast?.type}
        handleClose={() => setToast(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        autoHideDuration={3000}
      />
    </Box>
  );
};

export default Login;
